import { AppConfig } from '@core/domain/AppConfig.ts';
import { withRetry } from '@core/domain/utils/asyncResilience.ts';
import { noteLocalStorageQuotaExceeded } from '@src/modules/platform/local-media/domain/localStorageHeadroom.ts';

export const LOCAL_MEDIA_IDB_STORE = 'media-blobs';

export type SharedLocalIdbConfig = {
  dbName: string;
  dbVersion: number;
  kvStore: string;
};

const MEDIA_RETRY_OPTS = {
  maxAttempts: 5,
  baseDelayMs: 50,
  maxDelayMs: 500,
  jitterRatio: 0.2,
} as const;

const READINESS_PROBE_KEY = '__esan_media_ready_probe__';

let testOverride: SharedLocalIdbConfig | null = null;
let sharedDbPromise: Promise<IDBDatabase> | null = null;
let sharedDbConfigKey = '';
let resolvedDbVersion: number | null = null;

/** @internal test hook */
export function configureSharedLocalIdbForTests(config: SharedLocalIdbConfig | null): void {
  testOverride = config;
  resolvedDbVersion = null;
  invalidateSharedLocalIdb();
}

function resolveConfig(): SharedLocalIdbConfig {
  const base = testOverride ?? {
    dbName: AppConfig.getLocalSqliteIdbName(),
    dbVersion: AppConfig.getLocalSqliteIdbVersion(),
    kvStore: AppConfig.getLocalSqliteIdbStore(),
  };
  return resolvedDbVersion != null ? { ...base, dbVersion: resolvedDbVersion } : base;
}

function idbHasRequiredStores(idb: IDBDatabase, config: SharedLocalIdbConfig): boolean {
  return idb.objectStoreNames.contains(config.kvStore) && idb.objectStoreNames.contains(LOCAL_MEDIA_IDB_STORE);
}

function configKey(config: SharedLocalIdbConfig): string {
  return `${config.dbName}:${config.dbVersion}:${config.kvStore}`;
}

export function invalidateSharedLocalIdb(): void {
  sharedDbPromise = null;
  sharedDbConfigKey = '';
}

export function isRetryableIdbError(err: unknown): boolean {
  if (err instanceof DOMException) {
    if (err.name === 'QuotaExceededError') return false;
    return ['InvalidStateError', 'TransactionInactiveError', 'AbortError', 'UnknownError', 'NotFoundError'].includes(
      err.name,
    );
  }
  if (err instanceof Error) {
    const message = err.message.toLowerCase();
    return message.includes('indexeddb') || message.includes('transaction') || message.includes('connection');
  }
  return true;
}

export function idbUserMessage(err: unknown, fallback: string): Error {
  if (err instanceof DOMException) {
    if (err.name === 'QuotaExceededError') {
      noteLocalStorageQuotaExceeded();
      return new Error('No hay espacio en el dispositivo para guardar la firma o la foto.');
    }
    if (err.name === 'InvalidStateError') {
      return new Error('Almacenamiento local no disponible. Cierre y vuelva a abrir la app.');
    }
    if (err.name === 'NotFoundError') {
      return new Error('Almacenamiento de fotos y firmas no está listo. Actualice la app y vuelva a abrirla.');
    }
    if (err.message) return new Error(err.message);
  }
  if (err instanceof Error && err.message) return err;
  return new Error(fallback);
}

function bindLifecycle(idb: IDBDatabase, onInvalidate: () => void): IDBDatabase {
  idb.onclose = onInvalidate;
  idb.onversionchange = () => {
    idb.close();
    onInvalidate();
  };
  return idb;
}

function openOnce(config: SharedLocalIdbConfig, repairAttempt = 0): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(config.dbName, config.dbVersion);
    req.onerror = () => reject(idbUserMessage(req.error, 'No se pudo abrir el almacenamiento local.'));
    req.onupgradeneeded = () => {
      const idb = req.result;
      if (!idb.objectStoreNames.contains(config.kvStore)) idb.createObjectStore(config.kvStore);
      if (!idb.objectStoreNames.contains(LOCAL_MEDIA_IDB_STORE)) idb.createObjectStore(LOCAL_MEDIA_IDB_STORE);
    };
    req.onsuccess = () => {
      try {
        const idb = req.result;
        if (!idbHasRequiredStores(idb, config)) {
          if (repairAttempt >= 3) {
            idb.close();
            invalidateSharedLocalIdb();
            reject(new Error('Almacenamiento de fotos y firmas no está listo. Actualice la app y vuelva a abrirla.'));
            return;
          }
          const nextVersion = Math.max(config.dbVersion, idb.version) + 1;
          resolvedDbVersion = nextVersion;
          idb.close();
          invalidateSharedLocalIdb();
          resolve(openOnce({ ...config, dbVersion: nextVersion }, repairAttempt + 1));
          return;
        }
        resolvedDbVersion = idb.version;
        resolve(bindLifecycle(idb, invalidateSharedLocalIdb));
      } catch (err) {
        invalidateSharedLocalIdb();
        reject(idbUserMessage(err, 'No se pudo abrir el almacenamiento local.'));
      }
    };
  });
}

export function openSharedLocalIdb(): Promise<IDBDatabase> {
  const config = resolveConfig();
  const key = configKey(config);
  if (!sharedDbPromise || sharedDbConfigKey !== key) {
    sharedDbConfigKey = key;
    sharedDbPromise = openOnce(config);
  }
  return sharedDbPromise;
}

async function runIdbTxAttempt(
  storeName: string,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => void,
  fallback: string,
): Promise<void> {
  const idb = await openSharedLocalIdb();
  await new Promise<void>((resolve, reject) => {
    const tx = idb.transaction(storeName, mode);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error(fallback));
    run(tx.objectStore(storeName));
  });
}

export async function runSharedIdbTx(
  storeName: string,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => void,
  fallback: string,
): Promise<void> {
  try {
    await withRetry(
      async () => {
        await runIdbTxAttempt(storeName, mode, run, fallback);
      },
      {
        ...MEDIA_RETRY_OPTS,
        isRetryable: (err) => {
          if (!isRetryableIdbError(err)) return false;
          invalidateSharedLocalIdb();
          return true;
        },
      },
    );
  } catch (err) {
    throw idbUserMessage(err, fallback);
  }
}

export async function runSharedIdbRead<T>(
  storeName: string,
  run: (store: IDBObjectStore) => IDBRequest<T>,
  fallback: string,
): Promise<T> {
  try {
    return await withRetry(
      async () => {
        const idb = await openSharedLocalIdb();
        return await new Promise<T>((resolve, reject) => {
          const tx = idb.transaction(storeName, 'readonly');
          const req = run(tx.objectStore(storeName));
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error ?? new Error(fallback));
          tx.onerror = () => reject(tx.error ?? new Error(fallback));
        });
      },
      {
        ...MEDIA_RETRY_OPTS,
        isRetryable: (err) => {
          if (!isRetryableIdbError(err)) return false;
          invalidateSharedLocalIdb();
          return true;
        },
      },
    );
  } catch (err) {
    throw idbUserMessage(err, fallback);
  }
}

/** Boot probe: round-trip one blob in media-blobs. */
export async function ensureLocalMediaStorageReady(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  await withRetry(
    async () => {
      const probe = new Blob(['ok'], { type: 'text/plain' });
      await runSharedIdbTx(
        LOCAL_MEDIA_IDB_STORE,
        'readwrite',
        (store) => {
          store.put(probe, READINESS_PROBE_KEY);
        },
        'Almacenamiento de fotos y firmas no está listo.',
      );
      const stored = await runSharedIdbRead<Blob | undefined>(
        LOCAL_MEDIA_IDB_STORE,
        (store) => store.get(READINESS_PROBE_KEY),
        'Almacenamiento de fotos y firmas no está listo.',
      );
      if (!stored?.size) {
        throw new Error('Almacenamiento de fotos y firmas no está listo. Actualice la app y vuelva a abrirla.');
      }
      await runSharedIdbTx(
        LOCAL_MEDIA_IDB_STORE,
        'readwrite',
        (store) => {
          store.delete(READINESS_PROBE_KEY);
        },
        'Almacenamiento de fotos y firmas no está listo.',
      );
    },
    {
      maxAttempts: 5,
      baseDelayMs: 100,
      maxDelayMs: 800,
      jitterRatio: 0.2,
      isRetryable: (err) => {
        if (!isRetryableIdbError(err)) return false;
        invalidateSharedLocalIdb();
        return true;
      },
    },
  );
}
